import { useEffect, useState } from 'react';
import { Modal } from '@/components/dialogs/Modal';
import { Spinner } from '@/components/common/Spinner';
import { ErrorState } from '@/components/common/ErrorState';
import type { GetPermissionListResultDto, UpdatePermissionsDto } from '@/api/types/identity';
import { PermissionTree, initialGrants } from './PermissionTree';

interface PermissionsModalProps {
  show: boolean;
  title: string;
  isLoading: boolean;
  isError: boolean;
  result?: GetPermissionListResultDto;
  busy?: boolean;
  onSave: (input: UpdatePermissionsDto) => void;
  onClose: () => void;
  onRetry: () => void;
}

// Reusable permission-management modal mirroring the legacy Users/Roles
// Permissions page: grouped cards, cascade, filter, Save/Cancel.
export function PermissionsModal({
  show,
  title,
  isLoading,
  isError,
  result,
  busy = false,
  onSave,
  onClose,
  onRetry,
}: PermissionsModalProps) {
  const [grants, setGrants] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (result) setGrants(initialGrants(result));
  }, [result]);

  function handleSave() {
    if (!result) return;
    const permissions = result.groups
      .flatMap((g) => g.permissions)
      .map((p) => ({ name: p.name, isGranted: grants[p.name] ?? false }));
    onSave({ permissions });
  }

  return (
    <Modal
      show={show}
      title={title}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-light" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={busy || isLoading || isError || !result}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : isError || !result ? (
        <ErrorState message="Could not load permissions." onRetry={onRetry} />
      ) : (
        <PermissionTree result={result} grants={grants} onToggle={setGrants} />
      )}
    </Modal>
  );
}
